import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { DialogFooter } from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { InvestmentAccount } from "@/hooks/useInvestments";
import { Account } from "@accountHooks";
import { format } from "date-fns";
import { AlertCircle } from "lucide-react";
import { getDisplayName, disambiguateDisplayNames } from "@/lib/display-name";
import { useCurrencyList } from "@/hooks/useCurrencyList";

const investmentTransactionFormSchema = z
  .object({
    transaction_type: z.enum(["deposit", "withdrawal", "interest"]),
    investment_account_id: z.string().min(1, "Investment account is required"),
    amount: z.number().positive("Amount must be greater than 0"),
    currency: z.string().min(1, "Currency is required"),
    source_account_id: z.string().optional(),
    destination_account_id: z.string().optional(),
    withdrawal_fee: z
      .number()
      .min(0, "Withdrawal fee cannot be negative")
      .optional(),
    deposit_fee: z
      .number()
      .min(0, "Deposit fee cannot be negative")
      .optional(),
    transaction_date: z.string().min(1, "Transaction date is required"),
    notes: z
      .string()
      .max(500, "Notes must be 500 characters or less")
      .optional(),
    description: z
      .string()
      .max(500, "Description must be 500 characters or less")
      .optional(),
  })
  .refine(
    (data) => {
      // For deposits, source_account_id is required
      if (data.transaction_type === "deposit") {
        return !!data.source_account_id;
      }
      return true;
    },
    {
      message: "Source account is required for deposits",
      path: ["source_account_id"],
    },
  )
  .refine(
    (data) => {
      // For withdrawals, destination_account_id is required
      if (data.transaction_type === "withdrawal") {
        return !!data.destination_account_id;
      }
      return true;
    },
    {
      message: "Destination account is required for withdrawals",
      path: ["destination_account_id"],
    },
  );

export type InvestmentTransactionFormData = z.infer<
  typeof investmentTransactionFormSchema
>;

interface InvestmentTransactionFormProps {
  transactionType: "deposit" | "withdrawal" | "interest";
  investments: InvestmentAccount[];
  accounts: Account[];
  onSave: (data: InvestmentTransactionFormData) => void | Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  defaultInvestmentId?: string; // Optional pre-selected investment account
}

/**
 * InvestmentTransactionForm component for creating investment transactions (deposits and withdrawals).
 * Handles balance validation, currency conversion warnings, and linked transaction creation.
 *
 * @param transactionType - Type of transaction: 'deposit' or 'withdrawal'
 * @param investments - List of available investment accounts
 * @param accounts - List of available user accounts
 * @param onSave - Callback function when form is submitted with valid data
 * @param onCancel - Callback function when form is cancelled
 * @param isSubmitting - External submission state indicator
 *
 * @example
 * <InvestmentTransactionForm
 *   transactionType="deposit"
 *   investments={investments}
 *   accounts={accounts}
 *   onSave={handleSave}
 *   onCancel={handleCancel}
 * />
 */
export function InvestmentTransactionForm({
  transactionType: initialTransactionType,
  investments,
  accounts,
  onSave,
  onCancel,
  isSubmitting = false,
  defaultInvestmentId,
}: InvestmentTransactionFormProps) {
  // Find the default investment to get its currency
  const defaultInvestment = defaultInvestmentId
    ? investments.find((inv) => inv.id === defaultInvestmentId)
    : undefined;
  const {
    data: availableCurrencies,
    isLoading: isLoadingCurrencies,
    error: errorLoadingCurrencies,
  } = useCurrencyList();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting: formIsSubmitting },
  } = useForm<InvestmentTransactionFormData>({
    resolver: zodResolver(investmentTransactionFormSchema),
    defaultValues: {
      transaction_type: initialTransactionType,
      investment_account_id: defaultInvestmentId || "",
      amount: 0,
      currency: defaultInvestment?.currency || "USD",
      source_account_id: "",
      destination_account_id: "",
      withdrawal_fee: 0,
      deposit_fee: 0,
      transaction_date: format(new Date(), "yyyy-MM-dd"),
      notes: "",
      description: "",
    },
  });

  const transactionType = watch("transaction_type");
  const investmentAccountId = watch("investment_account_id");
  const currency = watch("currency");
  const sourceAccountId = watch("source_account_id");
  const destinationAccountId = watch("destination_account_id");
  const amount = watch("amount");
  const withdrawalFee = watch("withdrawal_fee");
  const depositFee = watch("deposit_fee");

  // Find selected investment account
  const selectedInvestment = investments.find(
    (inv) => inv.id === investmentAccountId,
  );

  // Disambiguated account labels for selects
  const disambiguatedAccounts = disambiguateDisplayNames(
    accounts.map((a) => ({ ...a, displayName: getDisplayName(a) })),
  );

  // Find selected source/destination account
  const selectedSourceAccount = accounts.find(
    (acc) => acc.id === sourceAccountId,
  );
  const selectedDestinationAccount = accounts.find(
    (acc) => acc.id === destinationAccountId,
  );

  // Check if currencies differ (for conversion warning)
  const showCurrencyWarning =
    selectedInvestment &&
    ((transactionType === "deposit" &&
      selectedSourceAccount &&
      currency !== selectedSourceAccount.currency) ||
      (transactionType === "withdrawal" &&
        selectedDestinationAccount &&
        currency !== selectedDestinationAccount.currency));

  // Check for insufficient funds (includes deposit fee for deposits)
  const hasInsufficientFunds =
    (transactionType === "deposit" &&
      selectedSourceAccount &&
      amount + (depositFee || 0) > selectedSourceAccount.balance) ||
    (transactionType === "withdrawal" &&
      selectedInvestment &&
      amount + (withdrawalFee || 0) > selectedInvestment.balance);

  // Update currency when investment account changes
  const handleInvestmentAccountChange = (value: string) => {
    setValue("investment_account_id", value);
    const investment = investments.find((inv) => inv.id === value);
    if (investment) {
      setValue("currency", investment.currency);
    }
  };

  const onSubmit = async (data: InvestmentTransactionFormData) => {
    await onSave(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label>Transaction Type</Label>
        <Select
          value={transactionType}
          onValueChange={(value) => {
            setValue(
              "transaction_type",
              value as "deposit" | "withdrawal" | "interest",
            );
            // Clear account selections when type changes
            setValue("source_account_id", "");
            setValue("destination_account_id", "");
          }}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="deposit">Deposit</SelectItem>
            <SelectItem value="withdrawal">Withdrawal</SelectItem>
            <SelectItem value="interest">Interest</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="investment_account_id">Investment Account *</Label>
        <Select
          value={investmentAccountId}
          onValueChange={handleInvestmentAccountChange}>
          <SelectTrigger id="investment_account_id">
            <SelectValue placeholder="Select investment account" />
          </SelectTrigger>
          <SelectContent>
            {investments.map((inv) => (
              <SelectItem key={inv.id} value={inv.id}>
                {inv.fund_name} - {inv.fund_manager}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.investment_account_id && (
          <p className="text-sm text-destructive">
            {errors.investment_account_id.message}
          </p>
        )}
        {selectedInvestment && (
          <p className="text-xs text-muted-foreground">
            Current balance:{" "}
            {selectedInvestment.balance.toLocaleString(undefined, {
              style: "currency",
              currency: selectedInvestment.currency,
            })}
          </p>
        )}
      </div>

      {transactionType === "deposit" && (
        <div className="space-y-2">
          <Label htmlFor="source_account_id">Source Account *</Label>
          <Select
            value={sourceAccountId}
            onValueChange={(value) => setValue("source_account_id", value)}>
            <SelectTrigger id="source_account_id">
              <SelectValue placeholder="Select source account" />
            </SelectTrigger>
            <SelectContent>
              {disambiguatedAccounts.map((acc) => (
                <SelectItem key={acc.id} value={acc.id}>
                  {acc.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.source_account_id && (
            <p className="text-sm text-destructive">
              {errors.source_account_id.message}
            </p>
          )}
          {selectedSourceAccount && (
            <p className="text-xs text-muted-foreground">
              Current balance:{" "}
              {selectedSourceAccount.balance.toLocaleString(undefined, {
                style: "currency",
                currency: selectedSourceAccount.currency,
              })}
            </p>
          )}
        </div>
      )}

      {transactionType === "withdrawal" && (
        <div className="space-y-2">
          <Label htmlFor="destination_account_id">Destination Account *</Label>
          <Select
            value={destinationAccountId}
            onValueChange={(value) =>
              setValue("destination_account_id", value)
            }>
            <SelectTrigger id="destination_account_id">
              <SelectValue placeholder="Select destination account" />
            </SelectTrigger>
            <SelectContent>
              {disambiguatedAccounts.map((acc) => (
                <SelectItem key={acc.id} value={acc.id}>
                  {acc.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.destination_account_id && (
            <p className="text-sm text-destructive">
              {errors.destination_account_id.message}
            </p>
          )}
          {selectedDestinationAccount && (
            <p className="text-xs text-muted-foreground">
              Current balance:{" "}
              {selectedDestinationAccount.balance.toLocaleString(undefined, {
                style: "currency",
                currency: selectedDestinationAccount.currency,
              })}
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="amount">Amount *</Label>
          <Input
            id="amount"
            type="number"
            step="0.01"
            min="0.01"
            {...register("amount", { valueAsNumber: true })}
            placeholder="e.g. 1000"
          />
          {errors.amount && (
            <p className="text-sm text-destructive">{errors.amount.message}</p>
          )}
          {hasInsufficientFunds && (
            <p className="text-sm text-destructive flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              Insufficient funds
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="transaction_date">Date *</Label>
          <Input
            id="transaction_date"
            type="date"
            {...register("transaction_date")}
          />
          {errors.transaction_date && (
            <p className="text-sm text-destructive">
              {errors.transaction_date.message}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label htmlFor="currency">Currency *</Label>
          {showCurrencyWarning && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    Currency conversion will be applied. The amount will be
                    converted from {currency} to{" "}
                    {transactionType === "deposit"
                      ? selectedInvestment?.currency
                      : selectedDestinationAccount?.currency}{" "}
                    using current exchange rates.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <Select
          value={currency}
          onValueChange={(value) => setValue("currency", value)}>
          <SelectTrigger id="currency">
            <SelectValue />
          </SelectTrigger>
          {isLoadingCurrencies && "Loading Currencies"}
          {!isLoadingCurrencies && !errorLoadingCurrencies ? (
            <SelectContent>
              {availableCurrencies.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          ) : (
            "Failed to load currencies"
          )}
        </Select>
        {errors.currency && (
          <p className="text-sm text-destructive">{errors.currency.message}</p>
        )}
      </div>

      {transactionType === "withdrawal" && (
        <div className="space-y-2">
          <Label htmlFor="withdrawal_fee">Withdrawal Fee</Label>
          <Input
            id="withdrawal_fee"
            type="number"
            step="0.01"
            min="0"
            {...register("withdrawal_fee", { valueAsNumber: true })}
            placeholder="0.00"
          />
          {errors.withdrawal_fee && (
            <p className="text-sm text-destructive">
              {errors.withdrawal_fee.message}
            </p>
          )}
        </div>
      )}

      {transactionType === "deposit" && (
        <div className="space-y-2">
          <Label htmlFor="deposit_fee">
            Deposit Fee{" "}
            <span className="text-muted-foreground font-normal">(optional)</span>
          </Label>
          <Input
            id="deposit_fee"
            type="number"
            step="0.01"
            min="0"
            {...register("deposit_fee", { valueAsNumber: true })}
            placeholder="0.00"
          />
          <p className="text-xs text-muted-foreground">
            Fee charged on the source account in addition to the deposit amount.
          </p>
          {errors.deposit_fee && (
            <p className="text-sm text-destructive">
              {errors.deposit_fee.message}
            </p>
          )}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Input
          id="description"
          {...register("description")}
          placeholder="Brief description of the transaction"
          maxLength={500}
        />
        {errors.description && (
          <p className="text-sm text-destructive">
            {errors.description.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          {...register("notes")}
          placeholder="Additional notes..."
          rows={2}
          maxLength={500}
        />
        {errors.notes && (
          <p className="text-sm text-destructive">{errors.notes.message}</p>
        )}
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting || formIsSubmitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={
            hasInsufficientFunds ||
            isSubmitting ||
            formIsSubmitting ||
            !["deposit", "withdrawal", "interest"].includes(transactionType)
          }>
          {isSubmitting || formIsSubmitting
            ? "Processing..."
            : (
              <>
                {transactionType === "deposit" && "Deposit"}
                {transactionType === "withdrawal" && "Withdraw"}
                {transactionType === "interest" && "Record Interest"}
              </>
            )}
        </Button>
      </DialogFooter>
    </form>
  );
}
